import React, { useState } from 'react';
import {
  Sparkles,
  ChevronDown,
  ChevronUp,
  Target,
  Shield,
  Phone,
  CheckCircle2,
  Loader2,
  Ban,
  Zap,
  MessageSquare,
  ThumbsDown,
  ListOrdered,
  Trophy,
  AlertCircle,
} from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import type { AIRecommendation, RecommendationLevel, NextStep } from '../utils/useMatchRecommendation';
import type { ConfidenceResult } from '../utils/matchConfidence';

interface AIMatchRecommendationProps {
  buyerId: string;
  propertyId: string;
  loading: boolean;
  error: string | null;
  recommendation: AIRecommendation | null;
  onGenerate: () => void;
  confidence: ConfidenceResult;
  matchScore: number;
  rankPosition?: number;
  totalQualityMatches?: number;
}

const LEVEL_STYLES: Record<RecommendationLevel, { dot: string; text: string; bg: string }> = {
  present_now: { dot: 'bg-green-500', text: 'text-green-400', bg: 'bg-green-900/15' },
  worth_presenting: { dot: 'bg-teal-500', text: 'text-teal-400', bg: 'bg-teal-900/15' },
  discuss_first: { dot: 'bg-amber-500', text: 'text-amber-400', bg: 'bg-amber-900/15' },
  weak_alternative: { dot: 'bg-gray-500', text: 'text-gray-400', bg: 'bg-gray-800/30' },
};

const LEVEL_LABELS: Record<RecommendationLevel, string> = {
  present_now: 'מומלץ להציג מיד',
  worth_presenting: 'שווה להציג ללקוח',
  discuss_first: 'כדאי לבדוק גמישות עם הלקוח',
  weak_alternative: 'חלופה אפשרית בלבד',
};

const NEXT_STEP_LABELS: Record<NextStep, string> = {
  present_immediately: 'להציג מיד',
  present_after_clarifying: 'להציג אחרי בירור נקודה אחת',
  keep_as_backup: 'לשמור כגיבוי',
  skip: 'לא להציג',
};

const CONFIDENCE_STYLES: Record<string, { text: string; bg: string }> = {
  high: { text: 'text-green-400', bg: 'bg-green-900/30' },
  medium: { text: 'text-amber-400', bg: 'bg-amber-900/30' },
  low: { text: 'text-red-400', bg: 'bg-red-900/30' },
};

const AIMatchRecommendation: React.FC<AIMatchRecommendationProps> = ({
  buyerId,
  propertyId,
  loading,
  error,
  recommendation,
  onGenerate,
  confidence,
  matchScore,
  rankPosition,
  totalQualityMatches,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleGenerate = () => {
    try {
      logEvent(analytics, 'ai_recommendation_opened', {
        buyer_id: buyerId,
        property_id: propertyId,
        match_score: matchScore,
        rank_position: rankPosition ?? null,
        total_quality_matches: totalQualityMatches ?? null,
      });
    } catch {
      // analytics is best-effort — never block the AI call
    }
    onGenerate();
  };

  if (!recommendation && !loading && !error) {
    return (
      <div className="mt-3 border border-gray-700 rounded-lg p-3">
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
        >
          <Sparkles size={16} />
          המלצת AI
        </button>
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <span>אמינות נתונים:</span>
          <span className={`px-2 py-0.5 rounded ${CONFIDENCE_STYLES[confidence.level].bg} ${CONFIDENCE_STYLES[confidence.level].text}`}>
            {confidence.label}
          </span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-3 border border-gray-700 rounded-lg p-3">
        <div className="flex items-center gap-2 text-sm text-blue-400">
          <Loader2 size={16} className="animate-spin" />
          <span>מכין אימון מכירות...</span>
        </div>
      </div>
    );
  }

  if (error && !recommendation) {
    return (
      <div className="mt-3 border border-gray-700 rounded-lg p-3">
        <p className="text-sm text-gray-400">
          לא ניתן להפיק כרגע המלצת AI. ציון ההתאמה עדיין זמין.
        </p>
        <button
          onClick={handleGenerate}
          className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          נסה שוב
        </button>
      </div>
    );
  }

  if (!recommendation) return null;

  const levelStyle = LEVEL_STYLES[recommendation.recommendation_level] || LEVEL_STYLES.weak_alternative;

  return (
    <div className={`mt-3 border border-gray-700 rounded-lg overflow-hidden ${levelStyle.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-blue-400" />
          <span className="text-sm font-semibold text-gray-200">מאמן המכירות של RV</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${levelStyle.dot}`} />
          <span className={`text-xs font-medium ${levelStyle.text}`}>
            {LEVEL_LABELS[recommendation.recommendation_level]}
          </span>
        </div>
      </div>

      {/* ── Compact section (always visible) ── */}
      <div className="px-3 pb-3 space-y-2.5">
        {/* Strategic opportunity */}
        {recommendation.strategic_opportunity && (
          <p className="text-sm text-gray-300 leading-relaxed">{recommendation.strategic_opportunity}</p>
        )}

        {/* Opening sentence */}
        {recommendation.opening_sentence && (
          <div className="flex items-start gap-2 bg-blue-900/20 border border-blue-800/40 rounded-md p-2.5">
            <Phone size={14} className="text-blue-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-gray-400 mb-0.5">פתיחת שיחה:</p>
              <p className="text-sm text-gray-200 leading-relaxed">{recommendation.opening_sentence}</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Expand toggle ── */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 hover:text-gray-300 hover:bg-gray-700/30 transition-colors border-t border-gray-700/50"
      >
        {expanded ? (
          <>
            <ChevronUp size={13} />
            <span>הסתר אימון מלא</span>
          </>
        ) : (
          <>
            <ChevronDown size={13} />
            <span>הצג אימון מכירות מלא</span>
          </>
        )}
      </button>

      {/* ── Expanded section ── */}
      {expanded && (
        <div className="px-3 pb-3 space-y-3 pt-2">
          {/* Section 1: האם להציג? */}
          <div className="flex items-center gap-2">
            <Target size={14} className="text-blue-400 shrink-0" />
            <span className="text-xs text-gray-400">האם להציג?</span>
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${levelStyle.bg} ${levelStyle.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${levelStyle.dot}`} />
              {LEVEL_LABELS[recommendation.recommendation_level]}
            </span>
          </div>

          {/* Section 2: טיעון מכירה מרכזי */}
          {recommendation.primary_selling_angle && (
            <div className="bg-amber-900/15 border border-amber-800/30 rounded-md p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Zap size={14} className="text-amber-400 shrink-0" />
                <span className="text-xs font-semibold text-gray-300">טיעון מכירה מרכזי</span>
              </div>
              <p className="text-sm text-gray-200 leading-relaxed">{recommendation.primary_selling_angle}</p>
            </div>
          )}

          {/* Section 3: איך פותחים את השיחה? */}
          {recommendation.opening_sentence && (
            <div className="flex items-start gap-2 bg-blue-900/20 border border-blue-800/40 rounded-md p-2.5">
              <Phone size={14} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-300 mb-0.5">איך פותחים את השיחה?</p>
                <p className="text-sm text-gray-200 leading-relaxed">{recommendation.opening_sentence}</p>
              </div>
            </div>
          )}

          {/* Section 4: סדר השיחה */}
          {recommendation.conversation_flow.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <ListOrdered size={14} className="text-teal-400 shrink-0" />
                <span className="text-xs font-semibold text-gray-300">סדר השיחה</span>
              </div>
              <div className="space-y-1">
                {recommendation.conversation_flow.map((s, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-teal-400 text-xs font-bold mt-0.5 shrink-0">{i + 1}.</span>
                    <span className="text-xs text-gray-300 leading-relaxed">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 5+6: התנגדות צפויה + תשובה */}
          {recommendation.most_likely_objection && (
            <div className="bg-gray-800/40 border border-gray-700/50 rounded-md p-2.5 space-y-2">
              <div className="flex items-center gap-1.5">
                <AlertCircle size={14} className="text-amber-400 shrink-0" />
                <span className="text-xs font-semibold text-gray-300">התנגדות צפויה</span>
              </div>
              <div className="flex items-start gap-1.5">
                <Ban size={12} className="text-red-400 mt-0.5 shrink-0" />
                <span className="text-xs text-gray-300 leading-relaxed">{recommendation.most_likely_objection}</span>
              </div>
              {recommendation.recommended_response && (
                <div className="flex items-start gap-1.5 pr-4 border-t border-gray-700/40 pt-2">
                  <MessageSquare size={12} className="text-teal-400 mt-0.5 shrink-0" />
                  <span className="text-xs text-teal-300/90 leading-relaxed">{recommendation.recommended_response}</span>
                </div>
              )}
            </div>
          )}

          {/* Section 7: למה לא לדבר */}
          {recommendation.avoid_talking_about.length > 0 && (
            <div className="bg-red-900/10 border border-red-800/30 rounded-md p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <ThumbsDown size={14} className="text-red-400 shrink-0" />
                <span className="text-xs font-semibold text-gray-300">למה לא לדבר</span>
              </div>
              <div className="space-y-1.5">
                {recommendation.avoid_talking_about.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-red-400 text-xs mt-0.5 shrink-0">✗</span>
                    <span className="text-xs text-gray-400 leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section 8: למה הנכס הזה קודם? */}
          {recommendation.why_this_property_first && (
            <div className="bg-gradient-to-l from-blue-900/25 to-teal-900/20 border border-blue-700/40 rounded-md p-3">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Trophy size={14} className="text-blue-400 shrink-0" />
                <span className="text-xs font-semibold text-blue-300">למה הנכס הזה קודם?</span>
              </div>
              <p className="text-sm text-gray-100 leading-relaxed">{recommendation.why_this_property_first}</p>
            </div>
          )}

          {/* Section 9: המלצה סופית */}
          {recommendation.final_recommendation && (
            <div className="flex items-start gap-2 bg-gray-700/40 rounded-md p-2.5">
              <CheckCircle2 size={14} className="text-blue-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-300">המלצה סופית</p>
                <p className="text-xs text-gray-200 mt-0.5 leading-relaxed">
                  {recommendation.final_recommendation}
                </p>
                {recommendation.recommended_next_step && (
                  <p className="text-xs text-blue-400 mt-1.5 font-medium">
                    {NEXT_STEP_LABELS[recommendation.recommended_next_step] || recommendation.recommended_next_step}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Data confidence badge */}
          <div className="space-y-1.5 pt-1 border-t border-gray-700/50">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Shield size={13} className="shrink-0" />
              <span>אמינות נתונים:</span>
              <span className={`px-2 py-0.5 rounded ${CONFIDENCE_STYLES[confidence.level].bg} ${CONFIDENCE_STYLES[confidence.level].text}`}>
                {confidence.label}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIMatchRecommendation;
